// Nutrition Calculator Trainer — case library + scoring helpers.
// Purely client-side, single-session. Pitched at Cert IV Veterinary Nursing.
//
// Energy maths taught:
//   RER (kcal) = 70 × kg^0.75   (linear 30×kg+70 for 2–45 kg also accepted within tolerance)
//   MER (kcal) = RER × life-stage / illness factor
//   Portion   = energy ÷ diet energy-density  (grams of dry via kcal/100 g, or cans via kcal/can)
//
// Numbers are computed at runtime (solveCase) from each case's inputs so the
// taught answer is always internally consistent.

export const rer = (kg) => 70 * Math.pow(kg, 0.75);

// Grade a typed number against a target: ≤10% = correct, ≤20% = partial, else wrong.
export const gradeNumeric = (answer, target, fullPts) => {
  const a = parseFloat(answer);
  if (isNaN(a) || !(target > 0)) return { band: "wrong", pts: 0 };
  const err = Math.abs(a - target) / target;
  if (err <= 0.1) return { band: "correct", pts: fullPts };
  if (err <= 0.2) return { band: "partial", pts: Math.round(fullPts * 0.5) };
  return { band: "wrong", pts: 0 };
};

// Map an option score (3 best / 1 partial / 0 wrong) onto a points budget.
export const optPts = (score, full, partial) =>
  score >= 3 ? full : score === 1 ? partial : 0;

// Derive every taught number for a case from its inputs.
export const solveCase = (c) => {
  const factorOpt = c.calc.factorOptions.find((o) => o.correct);
  const factor = factorOpt.value;
  const ew = c.energyWeightKg;
  const RER = rer(ew);
  const MER = RER * factor;
  const portionKcal = c.plan.portionRerMult ? RER * c.plan.portionRerMult : MER;
  const ed = c.diet.energyDensity;
  const grams = c.plan.portionMode === "dry" ? (portionKcal / ed.kcalPer100g) * 100 : null;
  const cans = c.plan.portionMode === "wet" ? portionKcal / ed.kcalPerCan : null;
  const ml = c.plan.portionMode === "liquid" ? portionKcal / ed.kcalPerMl : null;
  return { factor, ew, RER, MER, portionKcal, grams, cans, ml };
};

// Informational weight-loss timeline for weight-loss cases (non-graded).
export const weightLossPlan = (c) => {
  if (!c.weightLoss || !(c.weightKg > c.idealWeightKg)) return null;
  const ratePct = c.weightLoss.lossRatePctPerWeek;
  const weeklyLoss = c.weightKg * (ratePct / 100);
  const weeks = Math.max(1, Math.ceil((c.weightKg - c.idealWeightKg) / weeklyLoss));
  return {
    current: c.weightKg,
    ideal: c.idealWeightKg,
    ratePct,
    weeks,
    months: Math.max(1, Math.round(weeks / 4.345)),
  };
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Return a copy of a case with the selectable options shuffled (replay value).
export const serveCase = (c) => ({
  ...c,
  concerns: shuffle(c.concerns),
  calc: { ...c.calc, factorOptions: shuffle(c.calc.factorOptions) },
  diet: { ...c.diet, options: shuffle(c.diet.options) },
  plan: {
    ...c.plan,
    mealsOptions: shuffle(c.plan.mealsOptions),
    planOptions: shuffle(c.plan.planOptions),
  },
  educate: { ...c.educate, options: shuffle(c.educate.options) },
});

export const NUTRITION_CASES = [
  // 1 ─ Obese cat · weight loss ───────────────────────────────────────────────
  {
    id: "obese_cat_wl",
    title: "Marmalade · feline weight loss",
    species: "cat",
    name: "Marmalade",
    breed: "Domestic Shorthair",
    signalment: "8 yr · male neutered · DSH",
    weightKg: 6.4,
    idealWeightKg: 4.5,
    bcs: 8,
    weightLoss: { lossRatePctPerWeek: 0.75 },
    energyWeightKg: 4.5,
    energyWeightNote: "Use the IDEAL weight (4.5 kg) — feeding to current weight would maintain the obesity.",
    summary: "Presented for an obesity consult. Owner wants a weight-loss plan.",
    history:
      "Marmalade is fed dry food left out all day plus 'a bit of whatever we're having'. He gets several Dreamies daily and laps any leftover milk. He's indoor-only and sleeps most of the day. He weighs 6.4 kg; his ideal is about 4.5 kg.",
    concerns: [
      { t: "Food left out all day (free-fed)", flag: true, coach: "Free-feeding (ad-lib) prevents portion control — switch to measured meals." },
      { t: "Table scraps / human food", flag: true, coach: "Unmeasured extra calories undermine any weight-loss plan." },
      { t: "Daily cat treats", flag: true, coach: "Treats should be <10% of daily calories and counted in the plan." },
      { t: "Cow's milk offered", flag: true, coach: "Milk adds calories and most adult cats are lactose intolerant." },
      { t: "Indoor-only, low activity", flag: false, coach: "Relevant to energy needs but not a feeding-error red flag here." },
      { t: "Neutered", flag: false, coach: "Lowers energy requirement — accounted for by the factor, not a red flag." },
    ],
    calc: {
      factorOptions: [
        { value: 0.8, label: "×0.8 — Weight loss (feed 80% of RER at ideal weight)", correct: true, why: "Cats lose weight safely fed ~80% of RER calculated on their target weight." },
        { value: 1.2, label: "×1.2 — Neutered adult cat maintenance", correct: false, why: "Maintenance factor would hold weight, not reduce it." },
        { value: 1.0, label: "×1.0 — Inactive adult", correct: false, why: "Still too generous to drive weight loss in this cat." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 300, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Therapeutic weight-loss / metabolic diet (high protein, high fibre, calorie-restricted)", score: 3, note: "Keeps the cat satisfied and preserves lean mass while restricting calories." },
        { t: "Senior maintenance dry food, just less of it", score: 1, note: "Restricting a maintenance diet too far risks nutrient deficiency — a purpose-built weight-loss diet is better." },
        { t: "Grain-free 'natural' adult food, fed free-choice", score: 0, note: "Free-choice feeding and an energy-dense diet will not achieve weight loss." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the weight-loss energy target (80% RER at ideal weight).",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "2 measured meals per day (weighed, no top-ups)", score: 3 },
        { t: "Leave the daily amount out to graze", score: 0 },
        { t: "One large meal at night", score: 1 },
      ],
      planPrompt: "Set a safe weight-loss target rate.",
      planOptions: [
        { t: "~0.5–1% of body weight per week, with monthly weigh-ins", score: 3, note: "Safe feline rate; faster loss risks hepatic lipidosis." },
        { t: "3–4% per week to get there quickly", score: 0, note: "Too rapid — risks fatal hepatic lipidosis in cats." },
        { t: "Whatever happens, just feed less", score: 1, note: "No defined target or monitoring — likely to stall or be unsafe." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Weigh the daily ration, split into 2 meals, count treats within the calories, and recheck weight monthly so we can adjust.", score: 3, note: "Specific, measurable and safety-netted." },
        { t: "Just cut his food in half and he'll be fine.", score: 1, note: "Vague and risks over-restriction without monitoring." },
        { t: "Add lots of exercise and he can keep his treats and milk.", score: 0, note: "Ignores the main problem — calorie intake." },
      ],
    },
  },

  // 2 ─ Large-breed puppy · growth ─────────────────────────────────────────────
  {
    id: "lab_pup_growth",
    title: "Tank · large-breed puppy growth",
    species: "dog",
    name: "Tank",
    breed: "Labrador Retriever",
    signalment: "5 months · male entire · Labrador",
    weightKg: 18,
    idealWeightKg: 18,
    bcs: 5,
    energyWeightKg: 18,
    energyWeightNote: "Use the current weight — growing animals are fed to their present body weight.",
    summary: "Routine growth/feeding check for a fast-growing large-breed puppy.",
    history:
      "Tank is fed an adult all-life-stages food because 'he's already big'. The breeder advised adding a calcium supplement and extra meat for bone growth. He's bright, lean (BCS 5/9) and growing quickly.",
    concerns: [
      { t: "Fed adult food, not a growth diet", flag: true, coach: "Large-breed puppies need a controlled-calcium growth diet, not adult food." },
      { t: "Calcium supplement added", flag: true, coach: "Extra calcium in a large-breed pup risks developmental orthopaedic disease — never supplement a balanced growth diet." },
      { t: "Extra meat added to the bowl", flag: true, coach: "Unbalances the calcium:phosphorus ratio of a complete diet." },
      { t: "Bright and active", flag: false, coach: "Reassuring, not a red flag." },
      { t: "BCS 5/9 (ideal)", flag: false, coach: "Lean is exactly right for a growing large-breed pup." },
    ],
    calc: {
      factorOptions: [
        { value: 2.0, label: "×2.0 — Puppy older than 4 months (≈50% adult weight)", correct: true, why: "From ~4 months to maturity, growth energy is about 2 × RER." },
        { value: 3.0, label: "×3.0 — Puppy under 4 months", correct: false, why: "The ×3 factor is for very young pups (<4 months)." },
        { value: 1.6, label: "×1.6 — Neutered adult dog", correct: false, why: "Adult maintenance under-feeds a growing puppy." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 380, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Large-breed PUPPY growth diet (controlled calcium & energy)", score: 3, note: "Formulated to slow the growth rate and protect developing joints." },
        { t: "Standard puppy food + the calcium supplement", score: 1, note: "Right diet category, but the supplement is harmful — drop it." },
        { t: "Adult all-life-stages food with extra meat", score: 0, note: "Not optimised for controlled large-breed growth and is unbalanced." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the growth MER (RER × 2.0).",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "3 meals per day, adjusted as he grows", score: 3 },
        { t: "Once daily", score: 0 },
        { t: "Free-choice all day", score: 1 },
      ],
      planPrompt: "Best growth-monitoring plan?",
      planOptions: [
        { t: "Weigh and body-condition-score every 2–4 weeks; keep him lean and adjust the amount as he grows", score: 3, note: "Keeping large-breed pups lean reduces orthopaedic disease." },
        { t: "Feed to appetite so he grows as fast as possible", score: 0, note: "Rapid growth and over-conditioning damage developing joints." },
        { t: "Set the amount now and don't change it", score: 1, note: "A growing pup's needs change weekly — fixed amounts under/over-feed." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Switch to a large-breed puppy diet, stop the calcium and extra meat, feed 3 measured meals, and we'll weigh him every few weeks to keep him lean.", score: 3, note: "Corrects the diet and the harmful supplement with monitoring." },
        { t: "Adult food is fine since he's big; just keep the calcium up.", score: 0, note: "Reinforces both errors." },
        { t: "Any puppy food will do — don't worry about the breed.", score: 1, note: "Misses the large-breed controlled-growth point." },
      ],
    },
  },

  // 3 ─ CKD senior cat ─────────────────────────────────────────────────────────
  {
    id: "ckd_cat",
    title: "Pippa · chronic kidney disease",
    species: "cat",
    name: "Pippa",
    breed: "Domestic Shorthair",
    signalment: "14 yr · female spayed · DSH",
    weightKg: 3.8,
    idealWeightKg: 4.0,
    bcs: 4,
    energyWeightKg: 3.8,
    energyWeightNote: "Use the current weight — she is already slightly underweight, aim to maintain.",
    summary: "Newly diagnosed IRIS Stage 2 CKD. Mild weight loss, picky appetite.",
    history:
      "Pippa has been drinking and urinating more, has lost a little weight and is fussy with food. The owner has read online that cats need 'as much protein as possible' and wants to feed a high-protein kitten food. Her BCS is 4/9.",
    concerns: [
      { t: "Picky / reduced appetite", flag: true, coach: "Inappetence in CKD risks further weight loss — palatability and intake matter." },
      { t: "Recent weight loss", flag: true, coach: "Ongoing loss in a thin CKD cat needs an energy-adequate, palatable plan." },
      { t: "Owner wants high-protein kitten food", flag: true, coach: "A phosphorus-restricted renal diet (not max protein) is indicated in CKD." },
      { t: "Polyuria / polydipsia", flag: false, coach: "A clinical sign of CKD, but not a feeding red flag." },
      { t: "14 years old", flag: false, coach: "Age alone isn't the red flag here." },
    ],
    calc: {
      factorOptions: [
        { value: 1.2, label: "×1.2 — Neutered adult cat (maintain a thin cat)", correct: true, why: "Aim to maintain/gently regain — maintenance energy is appropriate, not restriction." },
        { value: 0.8, label: "×0.8 — Weight loss", correct: false, why: "She is already underweight — restricting calories would be harmful." },
        { value: 1.4, label: "×1.4 — Intact adult cat", correct: false, why: "She is spayed; this over-estimates her needs." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: null, kcalPerCan: 75, canLabel: "85 g can" },
      options: [
        { t: "Therapeutic renal diet (phosphorus-restricted, moderate high-quality protein), wet for hydration", score: 3, note: "Phosphorus restriction slows CKD progression; wet food helps hydration and intake." },
        { t: "Senior maintenance wet food", score: 1, note: "Better than kitten food, but lacks the phosphorus restriction that benefits CKD." },
        { t: "High-protein kitten food as the owner suggests", score: 0, note: "Excess phosphorus and protein load can worsen CKD." },
      ],
    },
    plan: {
      portionMode: "wet",
      portionNote: "Portion from the maintenance MER, fed as wet renal food.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "Several small warm meals through the day", score: 3 },
        { t: "One large meal daily", score: 0 },
        { t: "Dry food left out only", score: 1 },
      ],
      planPrompt: "Best appetite / transition plan?",
      planOptions: [
        { t: "Transition gradually, warm the food to boost aroma, ensure she keeps eating, and don't force strict restriction if it stops her eating", score: 3, note: "Maintaining intake is the priority — a cat that won't eat the renal diet does worse." },
        { t: "Switch abruptly and withhold all other food until she accepts it", score: 0, note: "Risks a food aversion and anorexia in a fragile CKD cat." },
        { t: "Let her free-feed any food she likes", score: 1, note: "Keeps her eating but loses the renal benefit and phosphorus control." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "A renal diet (not maximum protein) helps her kidneys; we'll transition slowly, warm the food, and keep a close eye on her weight and appetite.", score: 3, note: "Corrects the protein myth and prioritises intake and monitoring." },
        { t: "Feed her as much high-protein food as she'll take.", score: 0, note: "Reinforces the harmful protein myth." },
        { t: "Just pick any senior food, it doesn't really matter.", score: 1, note: "Misses the specific benefit of phosphorus restriction." },
      ],
    },
  },

  // 4 ─ Post-op anorexic dog · assisted feeding + refeeding trap ────────────────
  {
    id: "anorexic_dog_refeed",
    title: "Rosie · assisted feeding (post-op)",
    species: "dog",
    name: "Rosie",
    breed: "Kelpie",
    signalment: "5 yr · female spayed · Kelpie",
    weightKg: 16,
    idealWeightKg: 18,
    bcs: 3,
    energyWeightKg: 16,
    energyWeightNote: "Hospitalised / critical patients are fed to RER (factor ×1.0) initially.",
    summary: "Post-laparotomy, hasn't eaten for 5 days. An oesophagostomy tube is placed.",
    history:
      "Rosie had emergency surgery and has eaten nothing for 5 days. She is thin (BCS 3/9) and weak. The owner is keen for us to 'get a full day's food into her straight away' through the new feeding tube.",
    concerns: [
      { t: "No food for 5 days (prolonged anorexia)", flag: true, coach: "Prolonged starvation is the key risk factor for refeeding syndrome — reintroduce slowly." },
      { t: "Owner wants full feeds immediately", flag: true, coach: "Feeding full requirements on day 1 after starvation can trigger fatal refeeding syndrome." },
      { t: "Thin / weak (BCS 3/9)", flag: true, coach: "Depleted patients need careful, monitored reintroduction of calories." },
      { t: "Oesophagostomy tube placed", flag: false, coach: "Appropriate route for assisted feeding — not a red flag." },
      { t: "Recent surgery", flag: false, coach: "Relevant context, but the feeding red flag is the prolonged anorexia." },
    ],
    calc: {
      factorOptions: [
        { value: 1.0, label: "×1.0 — Hospitalised / critical (feed to RER)", correct: true, why: "Current evidence favours feeding critical patients at RER rather than illness multiples." },
        { value: 1.6, label: "×1.6 — Neutered adult maintenance", correct: false, why: "Maintenance multiples over-feed a debilitated, starved patient." },
        { value: 2.0, label: "×2.0 — 'Catch-up' feeding", correct: false, why: "Aggressive catch-up feeding is exactly what causes refeeding syndrome." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 110, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Critical-care / recovery convalescence diet (energy-dense, liquidisable for the tube)", score: 3, note: "Designed for assisted feeding — high energy and protein in a small, tube-friendly volume." },
        { t: "Liquidised adult maintenance food", score: 1, note: "Can work but is less energy-dense and harder to deliver the needs in small volumes." },
        { t: "A weight-loss diet because she'll be inactive", score: 0, note: "A depleted patient needs energy and protein, not restriction." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionRerMult: 0.3333,
      portionNote: "Day 1 of refeeding = about one-third of RER (split into small feeds).",
      mealsPrompt: "How should the daily amount be delivered?",
      mealsOptions: [
        { t: "Small volumes every 4–6 hours, warmed, flushing the tube before and after", score: 3 },
        { t: "One large bolus once a day", score: 0 },
        { t: "Two big feeds morning and night", score: 1 },
      ],
      planPrompt: "Best reintroduction (refeeding) plan?",
      planOptions: [
        { t: "Start at ~⅓ RER day 1, ~⅔ day 2, full RER by day 3; monitor electrolytes (phosphate, potassium)", score: 3, note: "Gradual reintroduction with electrolyte monitoring prevents refeeding syndrome." },
        { t: "Feed her full daily requirement today to rebuild her quickly", score: 0, note: "Classic refeeding-syndrome trap — can cause fatal electrolyte shifts." },
        { t: "Keep her nil-by-mouth another 2 days to be safe", score: 1, note: "Further starvation worsens depletion — she needs careful feeding now." },
      ],
    },
    educate: {
      prompt: "Best message for the owner?",
      options: [
        { t: "We'll reintroduce food slowly over a few days and monitor her bloods, because feeding too much too fast after starvation can be dangerous.", score: 3, note: "Explains the cautious, monitored approach and the why." },
        { t: "We'll give her a full day's food now to build her back up fast.", score: 0, note: "Reinforces the dangerous request." },
        { t: "We'll leave her tube unused until she feels like eating.", score: 1, note: "Misses the point of assisted feeding." },
      ],
    },
  },

  // 5 ─ Pancreatitis dog · low fat ─────────────────────────────────────────────
  {
    id: "pancreatitis_dog",
    title: "Biscuit · recovering pancreatitis",
    species: "dog",
    name: "Biscuit",
    breed: "Miniature Schnauzer",
    signalment: "7 yr · male neutered · Mini Schnauzer",
    weightKg: 9,
    idealWeightKg: 8.5,
    bcs: 6,
    energyWeightKg: 9,
    energyWeightNote: "Use the current weight; he is only mildly over-conditioned.",
    summary: "Recovering from acute pancreatitis, now eating again at home.",
    history:
      "Biscuit is a schnauzer recovering from pancreatitis. At home he's fed fatty table scraps and the occasional sausage, plus a standard adult food. The owner asks if a high-fat 'recovery' food will help him regain strength.",
    concerns: [
      { t: "Fatty table scraps", flag: true, coach: "Dietary fat is a major trigger for pancreatitis — scraps must stop." },
      { t: "Sausages / high-fat treats", flag: true, coach: "High-fat treats can precipitate another pancreatitis flare." },
      { t: "Owner wants a high-fat food", flag: true, coach: "A LOW-fat diet is indicated; high fat is contraindicated." },
      { t: "Mildly overweight (BCS 6/9)", flag: false, coach: "Worth addressing, but not the acute red flag here." },
      { t: "Schnauzer breed", flag: false, coach: "Predisposed to pancreatitis/hyperlipidaemia — context, not a feeding error." },
    ],
    calc: {
      factorOptions: [
        { value: 1.4, label: "×1.4 — Inactive / weight-conscious neutered dog", correct: true, why: "Recovering, mildly overweight neutered dog — a conservative maintenance factor." },
        { value: 1.8, label: "×1.8 — Intact active adult", correct: false, why: "Over-estimates needs for this neutered, inactive, slightly heavy dog." },
        { value: 1.0, label: "×1.0 — Weight loss", correct: false, why: "Not aiming for active weight loss during recovery; this under-feeds." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 330, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Low-fat, highly digestible gastrointestinal diet", score: 3, note: "Low fat is the cornerstone of pancreatitis dietary management." },
        { t: "Standard adult food, just no scraps", score: 1, note: "Removing scraps helps, but a purpose-made low-fat diet is safer." },
        { t: "High-fat 'recovery' food to build him up", score: 0, note: "High fat can trigger another pancreatitis episode." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the maintenance MER on a low-fat diet.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "3–4 small low-fat meals per day", score: 3 },
        { t: "One big meal daily", score: 1 },
        { t: "Free-choice with treats allowed", score: 0 },
      ],
      planPrompt: "Best ongoing management advice?",
      planOptions: [
        { t: "Strict low-fat diet only, no table scraps or fatty treats, small frequent meals", score: 3, note: "Consistent low-fat intake prevents recurrence." },
        { t: "Add some fat back gradually so he doesn't get bored", score: 0, note: "Reintroducing fat risks another flare." },
        { t: "Feed the low-fat food but scraps are fine in moderation", score: 1, note: "Even small fatty extras can trigger pancreatitis in a predisposed dog." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Stick to the low-fat diet, no fatty scraps or treats at all, and feed small frequent meals — fat is the main trigger we're avoiding.", score: 3, note: "Clear, correct and explains the why." },
        { t: "A high-fat recovery food will help him bounce back.", score: 0, note: "Directly contraindicated." },
        { t: "Just feed less of his normal food and the odd sausage is fine.", score: 1, note: "Still allows the high-fat trigger." },
      ],
    },
  },

  // 6 ─ Lactating queen ────────────────────────────────────────────────────────
  {
    id: "lactating_queen",
    title: "Willow · peak lactation",
    species: "cat",
    name: "Willow",
    breed: "Domestic Shorthair",
    signalment: "3 yr · female entire · DSH",
    weightKg: 4.0,
    idealWeightKg: 4.2,
    bcs: 5,
    energyWeightKg: 4.0,
    energyWeightNote: "Use the current weight; lactation hugely increases the factor.",
    summary: "Nursing a litter of 4 kittens at week 6 (peak lactation).",
    history:
      "Willow is nursing four 6-week-old kittens. The owner is feeding a measured amount of adult maintenance food twice a day and is worried she's 'getting too thin'. Her BCS is 5/9 but she's hungry and losing condition.",
    concerns: [
      { t: "Fed adult maintenance, not a growth/lactation diet", flag: true, coach: "Lactating queens need an energy-dense kitten/growth diet, fed generously." },
      { t: "Restricted to twice-daily measured meals", flag: true, coach: "Peak lactation usually needs free-choice (ad-lib) feeding." },
      { t: "Losing body condition while nursing", flag: true, coach: "Weight loss in lactation signals under-feeding — increase energy intake." },
      { t: "Nursing four kittens", flag: false, coach: "Drives the high requirement, but is normal, not a red flag." },
      { t: "BCS 5/9", flag: false, coach: "Currently ideal — the concern is the downward trend." },
    ],
    calc: {
      factorOptions: [
        { value: 3.0, label: "×3.0 — Peak lactation, ~4 kittens", correct: true, why: "Lactation needs roughly 2–4 × RER (more with larger litters) — ~3× is appropriate here." },
        { value: 1.2, label: "×1.2 — Neutered adult cat", correct: false, why: "Maintenance grossly under-feeds a lactating queen." },
        { value: 2.0, label: "×2.0 — Late gestation", correct: false, why: "Gestation factor is lower than peak lactation demands." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 430, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Energy-dense kitten / growth diet, fed free-choice", score: 3, note: "Highly digestible, energy- and nutrient-dense for lactation demands." },
        { t: "Adult maintenance food, but more of it", score: 1, note: "More calories help, but a growth diet better meets nutrient density." },
        { t: "A light/weight-control diet since she's a bit thin", score: 0, note: "The opposite of what a lactating, under-conditioned queen needs." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the lactation MER (RER × 3) as a starting point.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "Free-choice (ad-lib) kitten food, always available", score: 3 },
        { t: "Two measured meals a day", score: 0 },
        { t: "Three meals a day", score: 1 },
      ],
      planPrompt: "Best plan while she nurses?",
      planOptions: [
        { t: "Free-feed a kitten diet, monitor her weight, and keep it available until the kittens are weaned", score: 3, note: "Meets demand and lets the kittens start sampling solid food too." },
        { t: "Keep portions controlled so she doesn't gain weight", score: 0, note: "Under-feeding a lactating queen causes weight and milk loss." },
        { t: "Feed adult food twice daily as now", score: 1, note: "Insufficient energy density and frequency for lactation." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Switch her to a kitten diet and let her eat as much as she likes while nursing; we'll monitor her weight — extra demand now is completely normal.", score: 3, note: "Correct diet, free-choice, with reassurance and monitoring." },
        { t: "Keep her on measured adult meals so she doesn't get fat.", score: 0, note: "Reinforces the under-feeding." },
        { t: "Any food is fine as long as there's plenty of it.", score: 1, note: "Quantity helps but misses nutrient density of a growth diet." },
      ],
    },
  },

  // 7 ─ Diabetic dog ───────────────────────────────────────────────────────────
  {
    id: "diabetic_dog",
    title: "Duke · diabetic dog",
    species: "dog",
    name: "Duke",
    breed: "Beagle",
    signalment: "9 yr · male neutered · Beagle",
    weightKg: 13,
    idealWeightKg: 12,
    bcs: 6,
    energyWeightKg: 13,
    energyWeightNote: "Use the current weight; aim for gentle weight management toward ideal.",
    summary: "Newly stabilised diabetic on twice-daily insulin.",
    history:
      "Duke is a newly diagnosed diabetic on BID insulin. He's a notorious scavenger fed table scraps and given treats throughout the day, with meals 'whenever he seems hungry'. BCS 6/9.",
    concerns: [
      { t: "Treats given throughout the day", flag: true, coach: "Erratic snacking destabilises blood glucose in a diabetic." },
      { t: "Meals fed at irregular times", flag: true, coach: "Diabetic dogs need consistent meals timed with insulin injections." },
      { t: "Table scraps", flag: true, coach: "Unpredictable extra calories make glucose control difficult." },
      { t: "On twice-daily insulin", flag: false, coach: "Standard therapy — the issue is meal timing/consistency, not the insulin itself." },
      { t: "Slightly overweight (BCS 6/9)", flag: false, coach: "Worth managing, but not the acute feeding red flag." },
    ],
    calc: {
      factorOptions: [
        { value: 1.4, label: "×1.4 — Neutered, gentle weight management", correct: true, why: "Slightly over-conditioned neutered dog — a conservative factor supports lean weight and control." },
        { value: 1.8, label: "×1.8 — Intact active adult", correct: false, why: "Over-estimates needs for this neutered, slightly heavy dog." },
        { value: 1.0, label: "×1.0 — Strict weight loss", correct: false, why: "Aggressive restriction can complicate glycaemic stability while newly stabilised." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 310, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Consistent high-fibre / complex-carbohydrate diabetic diet", score: 3, note: "Slows glucose absorption and supports steady control and weight." },
        { t: "Standard adult food fed consistently at set times", score: 1, note: "Consistency helps; a fibre-rich diabetic diet improves control further." },
        { t: "High-energy food fed free-choice", score: 0, note: "Free-feeding and energy density wreck glycaemic control." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the maintenance MER, fed in equal meals.",
      mealsPrompt: "Feeding frequency / timing?",
      mealsOptions: [
        { t: "Two equal meals at the times of the insulin injections", score: 3 },
        { t: "Free-choice grazing", score: 0 },
        { t: "One meal a day", score: 1 },
      ],
      planPrompt: "Best management advice?",
      planOptions: [
        { t: "Feed equal, measured meals at the same times each day with the insulin; avoid extra treats and scraps", score: 3, note: "Consistency of amount and timing is the key to diabetic stability." },
        { t: "Skip the meal if he's not hungry but still give the insulin", score: 0, note: "Insulin without food risks dangerous hypoglycaemia." },
        { t: "Keep feeding treats so he stays happy", score: 1, note: "Treats undermine the consistency diabetics need." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Feed the same measured amount at the same two times every day, lined up with his insulin, and cut out the random treats and scraps.", score: 3, note: "Targets consistency — the core of diabetic feeding." },
        { t: "Just feed him whenever he's hungry, the insulin will sort it out.", score: 0, note: "Erratic feeding destabilises glucose and is unsafe." },
        { t: "Any low-calorie food is fine, timing doesn't matter much.", score: 1, note: "Timing with insulin matters a great deal in diabetics." },
      ],
    },
  },

  // 8 ─ Healthy adult dog · maintenance baseline ───────────────────────────────
  {
    id: "healthy_adult_dog",
    title: "Sandy · adult maintenance",
    species: "dog",
    name: "Sandy",
    breed: "Kelpie cross",
    signalment: "4 yr · female spayed · Kelpie x",
    weightKg: 20,
    idealWeightKg: 20,
    bcs: 5,
    energyWeightKg: 20,
    energyWeightNote: "Use the current weight; she is at her ideal body condition.",
    summary: "Healthy adult pet dog — owner wants a simple feeding plan.",
    history:
      "Sandy is a healthy, moderately active family dog at an ideal BCS 5/9. The owner currently free-feeds a complete adult dry food and adds a few dog biscuits, and just wants to know the right amount to feed.",
    concerns: [
      { t: "Free-feeding (food left out)", flag: true, coach: "Even healthy dogs do better on measured meals to maintain condition." },
      { t: "Extra biscuits added daily", flag: true, coach: "Treats should stay under ~10% of calories and be counted." },
      { t: "Complete & balanced adult food", flag: false, coach: "Appropriate diet choice — not a red flag." },
      { t: "Ideal BCS 5/9", flag: false, coach: "Exactly where we want her." },
      { t: "Moderately active", flag: false, coach: "Accounted for by the maintenance factor." },
    ],
    calc: {
      factorOptions: [
        { value: 1.6, label: "×1.6 — Neutered adult dog", correct: true, why: "Standard maintenance factor for a neutered adult pet dog." },
        { value: 1.8, label: "×1.8 — Intact adult dog", correct: false, why: "She is spayed — this over-estimates her needs." },
        { value: 1.0, label: "×1.0 — Weight loss", correct: false, why: "She is at ideal weight; restriction is unnecessary." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 360, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Complete & balanced adult maintenance diet (meets AAFCO/FEDIAF profile)", score: 3, note: "A complete and balanced adult food is exactly right for a healthy adult." },
        { t: "All-life-stages food fed by eye", score: 1, note: "Nutritionally fine, but 'by eye' feeding drifts toward over-feeding." },
        { t: "Home-cooked meat-only diet", score: 0, note: "An unbalanced single-ingredient diet risks nutrient deficiencies." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the maintenance MER (RER × 1.6).",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "Two measured meals per day", score: 3 },
        { t: "Leave food out all day", score: 0 },
        { t: "One meal a day", score: 1 },
      ],
      planPrompt: "Best ongoing plan?",
      planOptions: [
        { t: "Feed the measured amount in 2 meals, count biscuits within the calories, and adjust to keep her at BCS 5/9", score: 3, note: "Feed-to-condition is the gold standard for healthy adults." },
        { t: "Keep free-feeding and add treats freely", score: 0, note: "Drives gradual weight gain over time." },
        { t: "Fix the amount forever regardless of her body condition", score: 1, note: "Needs change with age/activity — adjust to BCS." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Feed this measured amount split into 2 meals, keep biscuits within her daily calories, and we'll tweak it to keep her at an ideal body condition.", score: 3, note: "Measured, feed-to-condition, treats counted." },
        { t: "Just keep the bowl topped up, she'll self-regulate.", score: 0, note: "Most dogs over-eat when free-fed." },
        { t: "Any amount is fine as long as she looks happy.", score: 1, note: "No objective target — condition can drift." },
      ],
    },
  },

  // 9 ─ Obese dog · weight loss (with timeline) ────────────────────────────────
  {
    id: "obese_dog_wl",
    title: "Bruno · canine weight loss",
    species: "dog",
    name: "Bruno",
    breed: "Staffordshire Bull Terrier",
    signalment: "6 yr · male neutered · Staffy",
    weightKg: 22,
    idealWeightKg: 17,
    bcs: 8,
    weightLoss: { lossRatePctPerWeek: 1.5 },
    energyWeightKg: 17,
    energyWeightNote: "Use the IDEAL weight (17 kg) and feed at RER — feeding to current weight maintains the obesity.",
    summary: "Obesity consult — owner wants a structured weight-loss plan.",
    history:
      "Bruno gets two big bowls of adult food a day, plus crusts, cheese and a daily pig's ear. He's walked rarely. He weighs 22 kg; his ideal is about 17 kg (BCS 8/9).",
    concerns: [
      { t: "Large unmeasured portions", flag: true, coach: "Feeding by eye almost always over-feeds — weigh the ration." },
      { t: "Cheese and table scraps", flag: true, coach: "Calorie-dense human foods sabotage any weight-loss plan." },
      { t: "Daily pig's ear (high-calorie chew)", flag: true, coach: "A single pig's ear can be 100+ kcal — a large hidden surplus." },
      { t: "Walked rarely", flag: false, coach: "Activity helps, but the calorie surplus is the main driver." },
      { t: "Neutered", flag: false, coach: "Lowers requirement — handled by the factor, not a red flag." },
    ],
    calc: {
      factorOptions: [
        { value: 1.0, label: "×1.0 — Weight loss (feed RER at ideal/target weight)", correct: true, why: "Dogs lose weight fed about RER calculated on their target weight." },
        { value: 1.6, label: "×1.6 — Neutered adult maintenance", correct: false, why: "Maintenance holds weight rather than reducing it." },
        { value: 1.4, label: "×1.4 — Inactive adult", correct: false, why: "Still too generous to drive weight loss." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 300, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Therapeutic weight-loss / metabolic diet (high protein & fibre, calorie-restricted)", score: 3, note: "Satisfies appetite and preserves lean mass while restricting calories." },
        { t: "His usual adult food, just smaller portions", score: 1, note: "Over-restricting a maintenance diet risks nutrient gaps." },
        { t: "Grain-free premium food fed by eye", score: 0, note: "Energy-dense and unmeasured — won't achieve loss." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from RER at the ideal weight.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "2 measured (weighed) meals per day", score: 3 },
        { t: "Free-choice in one big bowl", score: 0 },
        { t: "One meal a day", score: 1 },
      ],
      planPrompt: "Set a safe weight-loss target rate.",
      planOptions: [
        { t: "~1–2% of body weight per week with monthly weigh-ins", score: 3, note: "Safe canine rate that protects lean mass." },
        { t: "5% per week to speed things up", score: 0, note: "Too rapid — muscle loss and rebound." },
        { t: "No set target, just feed less", score: 1, note: "No measurable goal or monitoring." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Weigh the daily ration, split into 2 meals, swap the pig's ear and cheese for counted weight-loss food, and we'll weigh him monthly.", score: 3, note: "Specific, measurable and safety-netted." },
        { t: "Just walk him more and he can keep his treats.", score: 0, note: "Ignores the calorie surplus." },
        { t: "Halve his food and see how he goes.", score: 1, note: "Vague and unmonitored." },
      ],
    },
  },

  // 10 ─ Struvite / urinary cat (wet) ──────────────────────────────────────────
  {
    id: "struvite_cat",
    title: "Smokey · urinary / struvite",
    species: "cat",
    name: "Smokey",
    breed: "Domestic Shorthair",
    signalment: "5 yr · male neutered · DSH",
    weightKg: 5.0,
    idealWeightKg: 4.6,
    bcs: 6,
    energyWeightKg: 5.0,
    energyWeightNote: "Use the current weight; maintenance with a small reduction toward ideal.",
    summary: "Recurrent FLUTD with struvite crystals; first dietary consult.",
    history:
      "Smokey is fed dry food only and rarely drinks. He has had two episodes of struvite cystitis. The owner read that 'cats don't need much water' and offers no wet food.",
    concerns: [
      { t: "Dry-food-only diet", flag: true, coach: "Low moisture concentrates the urine and promotes crystals — wet food helps." },
      { t: "Rarely drinks water", flag: true, coach: "Low water intake is a key, modifiable struvite risk factor." },
      { t: "No wet food offered", flag: true, coach: "Wet food raises water intake and dilutes the urine." },
      { t: "Recurrent cystitis", flag: false, coach: "The clinical problem, not a feeding error itself." },
      { t: "Neutered male", flag: false, coach: "A risk signalment, but not a feeding red flag." },
    ],
    calc: {
      factorOptions: [
        { value: 1.2, label: "×1.2 — Neutered adult cat", correct: true, why: "Standard maintenance for a neutered adult cat." },
        { value: 0.8, label: "×0.8 — Weight loss", correct: false, why: "He is only slightly over ideal; this restricts too hard." },
        { value: 1.4, label: "×1.4 — Intact adult cat", correct: false, why: "He is neutered; this over-estimates his needs." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: null, kcalPerCan: 95, canLabel: "85 g can" },
      options: [
        { t: "Therapeutic urinary / struvite-dissolution diet, fed as WET food to boost water intake", score: 3, note: "Manages urine pH/minerals and the moisture raises water intake." },
        { t: "Urinary dry food only", score: 1, note: "Right diet type, but staying dry misses the water-intake benefit." },
        { t: "Standard adult dry food plus a water fountain", score: 0, note: "Doesn't address urinary mineral/pH management." },
      ],
    },
    plan: {
      portionMode: "wet",
      portionNote: "Portion from the maintenance MER as wet urinary food.",
      mealsPrompt: "Feeding / hydration plan?",
      mealsOptions: [
        { t: "Wet food across 2–3 meals, plus fresh water / a fountain", score: 3 },
        { t: "One dry meal a day", score: 0 },
        { t: "Free-choice dry only", score: 1 },
      ],
      planPrompt: "Best management advice?",
      planOptions: [
        { t: "Feed the wet urinary diet exclusively, maximise water intake, and recheck urine as advised", score: 3, note: "Moisture plus the therapeutic diet reduce recurrence." },
        { t: "Add extra minerals / 'urinary' supplements", score: 0, note: "Unbalances the carefully formulated diet." },
        { t: "Keep the dry food but add gravy sometimes", score: 1, note: "Half-measure — consistent moisture matters." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Switch to the wet urinary diet to get more water into him, feed it consistently, and we'll recheck his urine — moisture is a big part of preventing this.", score: 3, note: "Targets the moisture and the therapeutic diet." },
        { t: "Cats don't need much water — dry urinary food is enough.", score: 0, note: "Reinforces the moisture myth." },
        { t: "Any urinary food is fine, wet or dry won't matter.", score: 1, note: "Misses the water-intake advantage of wet food." },
      ],
    },
  },

  // 11 ─ Hepatic dog (copper-associated) ───────────────────────────────────────
  {
    id: "hepatic_dog",
    title: "Hazel · chronic hepatitis",
    species: "dog",
    name: "Hazel",
    breed: "Cocker Spaniel",
    signalment: "8 yr · female spayed · Cocker Spaniel",
    weightKg: 12,
    idealWeightKg: 12.5,
    bcs: 4,
    energyWeightKg: 12,
    energyWeightNote: "Use the current weight; she is mildly underweight, aim to maintain.",
    summary: "Chronic (copper-associated) hepatitis. New dietary plan.",
    history:
      "Hazel has chronic liver disease. The owner has drastically cut her protein 'to rest the liver' and feeds liver and organ meat as treats because they're 'good for the liver'. She has lost weight (BCS 4/9).",
    concerns: [
      { t: "Severe protein restriction", flag: true, coach: "Most liver patients need ADEQUATE high-quality protein — over-restriction causes muscle loss; restrict only with hepatic encephalopathy." },
      { t: "Feeding liver / organ meat", flag: true, coach: "Liver is very high in copper — harmful in copper-associated hepatopathy." },
      { t: "Recent weight loss", flag: true, coach: "Under-feeding worsens catabolism in liver disease — ensure enough calories." },
      { t: "On no other medication", flag: false, coach: "Not the feeding red flag here." },
      { t: "Spayed female", flag: false, coach: "Signalment, not a feeding error." },
    ],
    calc: {
      factorOptions: [
        { value: 1.4, label: "×1.4 — Neutered adult, maintain a thin dog", correct: true, why: "Provide enough energy to prevent further weight loss and catabolism." },
        { value: 1.0, label: "×1.0 — Weight loss", correct: false, why: "She is already underweight; restriction is harmful." },
        { value: 1.8, label: "×1.8 — Intact active adult", correct: false, why: "She is spayed and inactive; this over-estimates her needs." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 350, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Therapeutic hepatic diet (moderate high-quality protein, copper-restricted, energy-dense)", score: 3, note: "Supports the liver without starving her of protein or overloading copper." },
        { t: "Protein-restricted renal diet", score: 1, note: "Only appropriate if she has hepatic encephalopathy — not first-line here." },
        { t: "High-protein performance food with liver treats", score: 0, note: "Copper-rich and unbalanced for liver disease." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the maintenance MER on a hepatic diet.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "Several small meals through the day", score: 3 },
        { t: "One large meal daily", score: 1 },
        { t: "Free-choice with liver treats", score: 0 },
      ],
      planPrompt: "Best management advice?",
      planOptions: [
        { t: "Feed the hepatic diet exclusively, stop the liver/organ treats, and ensure she eats enough to regain condition", score: 3, note: "Adequate calories and copper control, no protein over-restriction." },
        { t: "Keep cutting protein further to protect the liver", score: 0, note: "Causes muscle wasting; restrict only with encephalopathy." },
        { t: "Hepatic food is fine but the odd liver treat won't hurt", score: 1, note: "Even small amounts of liver add significant copper." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "She actually needs enough good-quality protein and calories — we'll feed a copper-controlled hepatic diet, stop the liver treats, and build her weight back up.", score: 3, note: "Corrects both the protein and copper myths." },
        { t: "Keep protein as low as possible and the liver treats are healthy.", score: 0, note: "Reinforces both errors." },
        { t: "Any low-protein food will do.", score: 1, note: "Misses copper control and adequate energy." },
      ],
    },
  },

  // 12 ─ Critical-care liquid / mL tube feeding ────────────────────────────────
  {
    id: "critical_liquid_cat",
    title: "Misu · liquid tube feeding (ICU)",
    species: "cat",
    name: "Misu",
    breed: "Domestic Shorthair",
    signalment: "7 yr · female spayed · DSH",
    weightKg: 4.2,
    idealWeightKg: 4.4,
    bcs: 4,
    energyWeightKg: 4.2,
    energyWeightNote: "Hospitalised patient fed to RER (factor ×1.0).",
    summary: "Hospitalised, inappetent; naso-oesophageal tube placed for a liquid diet (day 3, now at full RER).",
    history:
      "Misu has a naso-oesophageal tube and is on a liquid critical-care diet. She is tolerating feeds well on day 3. The owner, visiting, asks if we can 'push a big feed through so she gets a full belly'.",
    concerns: [
      { t: "Owner wants large bolus feeds", flag: true, coach: "Large boluses through a narrow tube risk reflux, vomiting and aspiration — feed small volumes." },
      { t: "Inappetent / not eating voluntarily", flag: true, coach: "Justifies assisted feeding; keep delivering measured energy." },
      { t: "Narrow naso-oesophageal tube", flag: false, coach: "Route detail — it means liquid-only food and careful flushing." },
      { t: "Tolerating feeds on day 3", flag: false, coach: "Reassuring — she can be at full RER now." },
      { t: "Spayed female", flag: false, coach: "Signalment, not a feeding error." },
    ],
    calc: {
      factorOptions: [
        { value: 1.0, label: "×1.0 — Hospitalised (feed to RER)", correct: true, why: "Critical patients are fed at RER." },
        { value: 1.2, label: "×1.2 — Neutered adult cat maintenance", correct: false, why: "Maintenance multiples over-feed a critical patient." },
        { value: 2.0, label: "×2.0 — 'Catch-up' feeding", correct: false, why: "Aggressive feeding risks complications in a debilitated cat." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPerMl: 1.0, kcalPer100g: null, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Liquid critical-care / convalescence diet (designed to flow through a fine tube)", score: 3, note: "A liquid formula passes a narrow NO tube; energy-dense and complete." },
        { t: "Blended canned recovery food thinned with water", score: 1, note: "Can block a narrow NO tube — a purpose-made liquid diet is safer." },
        { t: "Blended dry food", score: 0, note: "Far too thick for a naso-oesophageal tube — it will block." },
      ],
    },
    plan: {
      portionMode: "liquid",
      portionNote: "Portion the RER as a liquid diet, delivered in mL.",
      mealsPrompt: "How should the volume be delivered?",
      mealsOptions: [
        { t: "Small measured volumes every 4–6 h, warmed, flushing before & after", score: 3 },
        { t: "One large bolus to fill her up", score: 0 },
        { t: "Two big feeds a day", score: 1 },
      ],
      planPrompt: "Best tube-feeding practice?",
      planOptions: [
        { t: "Check tube placement, give small warm aliquots, and flush with water before and after each feed", score: 3, note: "Prevents blockage, reflux and aspiration." },
        { t: "Push the full day's volume in one go so she isn't disturbed", score: 0, note: "Bolus over-distension risks vomiting and aspiration." },
        { t: "Skip the flushes to save time", score: 1, note: "Unflushed tubes block quickly." },
      ],
    },
    educate: {
      prompt: "Best message for the owner?",
      options: [
        { t: "We feed her little and often through the tube and flush it each time — small warm feeds are gentler on her stomach than one big one.", score: 3, note: "Explains the small-frequent rationale kindly." },
        { t: "Good idea — we'll push a big feed through now.", score: 0, note: "Unsafe bolus feeding." },
        { t: "We'll just wait until she eats on her own.", score: 1, note: "Defeats the purpose of the tube in an inappetent cat." },
      ],
    },
  },

  // 13 ─ Late-gestation bitch ───────────────────────────────────────────────────
  {
    id: "pregnant_bitch",
    title: "Sadie · late-gestation feeding",
    species: "dog",
    name: "Sadie",
    breed: "Golden Retriever",
    signalment: "4 yr · female entire · wk 7 of pregnancy",
    weightKg: 30,
    idealWeightKg: 30,
    bcs: 5,
    energyWeightKg: 30,
    energyWeightNote: "Use current weight; late gestation needs a raised energy factor on top of RER.",
    summary: "Pregnant at ~week 7 of gestation; owner asks how much to feed.",
    history:
      "Sadie is in the last third of pregnancy. The owner is still feeding her usual adult maintenance amount and asks if she needs more now and what to feed the pups later.",
    concerns: [
      { t: "Still on flat adult maintenance amount", flag: true, coach: "Energy needs climb steeply in the last third of gestation." },
      { t: "Fed adult maintenance diet", flag: true, coach: "Late gestation/lactation needs a growth/puppy or dedicated reproduction diet." },
      { t: "Owner unsure about meal frequency", flag: true, coach: "A large gravid uterus means smaller, more frequent meals are better tolerated." },
      { t: "Body condition currently ideal (BCS 5)", flag: false, coach: "Good starting condition — relevant context, not an error." },
    ],
    calc: {
      factorOptions: [
        { value: 1.8, label: "×1.8 — Late gestation (last third)", correct: true, why: "Energy demand rises markedly in the final third of pregnancy." },
        { value: 1.6, label: "×1.6 — Intact adult maintenance", correct: false, why: "Maintenance under-feeds a late-gestation bitch." },
        { value: 3.0, label: "×3.0 — Peak lactation", correct: false, why: "That's for nursing a large litter, not gestation." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 400, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Growth / puppy (or reproduction) diet — energy- and nutrient-dense", score: 3, note: "Supports gestation and the upcoming lactation; highly digestible." },
        { t: "Keep her on the same adult maintenance food", score: 1, note: "May lack the energy/nutrient density for late gestation and lactation." },
        { t: "Senior light diet to avoid her getting fat", score: 0, note: "Calorie-restricted senior food is wrong for a pregnant bitch." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the late-gestation energy target.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "Several small meals through the day", score: 3 },
        { t: "One large meal once daily", score: 0 },
        { t: "Free-feed unlimited food", score: 1 },
      ],
      planPrompt: "Plan for the weeks ahead?",
      planOptions: [
        { t: "Increase gradually now, then feed to lactation demand after whelping with free access for the queen and pups' creep feed later", score: 3, note: "Matches the steep rise into lactation safely." },
        { t: "Hold the amount steady right through nursing", score: 0, note: "Lactation needs far more energy than gestation." },
        { t: "Cut food back after whelping to lose the pregnancy weight", score: 0, note: "Dangerous during peak lactation demand." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Switch to a puppy/reproduction diet, feed several small meals now, and we'll increase further once the pups arrive and she's nursing.", score: 3, note: "Specific, staged and correct." },
        { t: "Just give her a bit extra of her normal food.", score: 1, note: "Vague and may not meet nutrient density needs." },
        { t: "Keep everything the same — she'll be fine.", score: 0, note: "Under-feeds late gestation and lactation." },
      ],
    },
  },

  // 14 ─ Senior cat maintenance (wet) ──────────────────────────────────────────
  {
    id: "senior_cat_maint",
    title: "Tabitha · senior cat maintenance",
    species: "cat",
    name: "Tabitha",
    breed: "Domestic Longhair",
    signalment: "14 yr · female neutered · DLH",
    weightKg: 4.0,
    idealWeightKg: 4.0,
    bcs: 5,
    energyWeightKg: 4.0,
    energyWeightNote: "Use current (ideal) weight with a senior maintenance factor.",
    summary: "Healthy senior cat for a wellness review; owner wants a feeding plan on canned food.",
    history:
      "Tabitha is bright, ideal weight and eats canned food. The owner asks how many cans a day are appropriate and how to feed a cat her age.",
    concerns: [
      { t: "No fresh water alongside dry-only previously", flag: true, coach: "Cats are prone to low water intake — wet food and water access support hydration." },
      { t: "Owner guessing portion sizes", flag: true, coach: "Portions should be calculated, not estimated." },
      { t: "Ideal body condition (BCS 5)", flag: false, coach: "Good condition — context, not an error." },
      { t: "Senior age", flag: false, coach: "Warrants monitoring but is normal context, not a feeding error." },
    ],
    calc: {
      factorOptions: [
        { value: 1.1, label: "×1.1 — Senior neutered cat maintenance", correct: true, why: "Older, less active neutered cats sit at the low end of maintenance." },
        { value: 1.6, label: "×1.6 — Active young cat", correct: false, why: "Too generous for a senior — would cause weight gain." },
        { value: 0.8, label: "×0.8 — Weight loss", correct: false, why: "She's ideal weight; no restriction needed." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: null, kcalPerCan: 95, canLabel: "85 g can" },
      options: [
        { t: "Complete senior wet diet, fed to a measured calorie target", score: 3, note: "Supports hydration and is balanced for an older cat." },
        { t: "Kitten food because she's underweight", score: 0, note: "She isn't underweight; kitten food is too energy-dense." },
        { t: "Free-choice dry food only", score: 1, note: "Risks overeating and lower water intake." },
      ],
    },
    plan: {
      portionMode: "wet",
      portionNote: "Work out how many cans cover the daily calorie target.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "2–3 small measured meals per day", score: 3 },
        { t: "One can dumped in the morning", score: 1 },
        { t: "Top up the bowl whenever it's empty", score: 0 },
      ],
      planPrompt: "Monitoring plan?",
      planOptions: [
        { t: "Recheck weight and body condition every few months and adjust; monitor appetite and water intake", score: 3, note: "Appropriate senior monitoring." },
        { t: "No need to monitor a healthy cat", score: 1, note: "Senior cats can change quickly — monitoring matters." },
        { t: "Only worry if she stops eating entirely", score: 0, note: "Too reactive; subtle changes are important in seniors." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Feed the calculated number of cans split into small meals, keep water available, and we'll recheck her weight every few months.", score: 3, note: "Specific, hydration-aware and monitored." },
        { t: "Just give her a couple of cans, roughly.", score: 1, note: "Unmeasured portions drift over time." },
        { t: "Leave food out so she can eat whenever.", score: 0, note: "Encourages overeating and is hard to monitor." },
      ],
    },
  },

  // 15 ─ Working dog · high energy demand ───────────────────────────────────────
  {
    id: "working_dog_energy",
    title: "Jet · working farm dog",
    species: "dog",
    name: "Jet",
    breed: "Border Collie",
    signalment: "5 yr · male neutered · active working dog",
    weightKg: 18,
    idealWeightKg: 18,
    bcs: 4,
    energyWeightKg: 18,
    energyWeightNote: "Use current weight; heavy daily work needs a high energy factor.",
    summary: "Hard-working farm dog losing condition; owner asks if he needs more food.",
    history:
      "Jet works long active days mustering and has dropped to a lean BCS 4. He's on a standard adult maintenance amount that hasn't changed despite his workload.",
    concerns: [
      { t: "Maintenance ration despite heavy daily work", flag: true, coach: "Working dogs need far more energy than a pet-maintenance ration." },
      { t: "Losing body condition (BCS 4)", flag: true, coach: "Dropping condition signals an energy deficit for his workload." },
      { t: "Standard adult diet (not performance)", flag: true, coach: "A high-energy/performance diet better meets a working dog's needs." },
      { t: "Neutered", flag: false, coach: "Lowers baseline needs slightly but is dwarfed by the work factor." },
    ],
    calc: {
      factorOptions: [
        { value: 3.0, label: "×3.0 — Heavy work / high activity", correct: true, why: "Hard-working dogs can need 2–5× RER; 3× suits sustained daily work." },
        { value: 1.6, label: "×1.6 — Neutered adult maintenance", correct: false, why: "Under-feeds a hard-working dog — hence his weight loss." },
        { value: 0.8, label: "×0.8 — Weight loss", correct: false, why: "He needs more energy, not restriction." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 450, kcalPerCan: null, canLabel: null },
      options: [
        { t: "High-energy performance/working-dog diet (energy- and fat-dense)", score: 3, note: "Delivers the calories in a manageable volume for a working dog." },
        { t: "Just feed a lot more of his standard food", score: 1, note: "May work but the large volume can be impractical and less digestible." },
        { t: "Senior light diet", score: 0, note: "Calorie-restricted — exactly wrong for his needs." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the heavy-work energy target.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "Split into meals around (not immediately before) work", score: 3 },
        { t: "One big meal right before a day's work", score: 0 },
        { t: "Free-feed all day", score: 1 },
      ],
      planPrompt: "Monitoring plan?",
      planOptions: [
        { t: "Increase to the work target, then recheck weight/condition and adjust with the season's workload", score: 3, note: "Energy needs vary with workload and weather." },
        { t: "Set it once and never change it", score: 1, note: "Working demand fluctuates — review regularly." },
        { t: "Only feed more on the days he looks tired", score: 0, note: "Reactive and won't rebuild lost condition." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Move to a performance diet at the higher target, feed around his work, and we'll recheck his weight to fine-tune for the season.", score: 3, note: "Specific, practical and monitored." },
        { t: "Just give him extra scoops when you remember.", score: 1, note: "Inconsistent and hard to track." },
        { t: "Keep it the same; working dogs are meant to be lean.", score: 0, note: "He's losing condition — that's an energy deficit to correct." },
      ],
    },
  },

  // 16 ─ Kitten · growth ────────────────────────────────────────────────────────
  {
    id: "kitten_growth",
    title: "Pip · kitten growth",
    species: "cat",
    name: "Pip",
    breed: "Domestic Shorthair",
    signalment: "12 wk · male entire · growing kitten",
    weightKg: 1.4,
    idealWeightKg: 1.4,
    bcs: 5,
    energyWeightKg: 1.4,
    energyWeightNote: "Use current weight; growing kittens need a high growth factor on RER.",
    summary: "New kitten check; owner wants feeding advice for healthy growth.",
    history:
      "Pip is a 12-week-old kitten doing well. The owner has been feeding adult cat food a couple of times a day and asks what and how much to feed for proper growth.",
    concerns: [
      { t: "Fed adult food rather than a kitten/growth diet", flag: true, coach: "Growing kittens need a complete kitten (growth) diet." },
      { t: "Only fed twice daily", flag: true, coach: "Young kittens benefit from more frequent small meals." },
      { t: "Portion estimated, not calculated", flag: true, coach: "Growth portions should be calculated and adjusted as they grow." },
      { t: "Good body condition (BCS 5)", flag: false, coach: "Healthy condition — context, not an error." },
    ],
    calc: {
      factorOptions: [
        { value: 2.5, label: "×2.5 — Kitten growth (<4 months)", correct: true, why: "Young kittens need roughly 2.5–3× RER for rapid growth." },
        { value: 1.2, label: "×1.2 — Neutered adult cat", correct: false, why: "Maintenance under-feeds a fast-growing kitten." },
        { value: 0.8, label: "×0.8 — Weight loss", correct: false, why: "Never restrict a growing kitten." },
      ],
    },
    diet: {
      prompt: "Choose the most appropriate diet.",
      energyDensity: { kcalPer100g: 420, kcalPerCan: null, canLabel: null },
      options: [
        { t: "Complete kitten / growth diet (energy- and nutrient-dense)", score: 3, note: "Formulated for the high demands of growth." },
        { t: "Adult maintenance food", score: 1, note: "Lacks the energy/nutrient profile for growth." },
        { t: "Senior or weight-control diet", score: 0, note: "Calorie-restricted — wrong for a growing kitten." },
      ],
    },
    plan: {
      portionMode: "dry",
      portionNote: "Portion from the growth energy target.",
      mealsPrompt: "Feeding frequency?",
      mealsOptions: [
        { t: "Several small meals through the day", score: 3 },
        { t: "One meal a day", score: 0 },
        { t: "Free-feed a measured daily amount of kitten food", score: 1 },
      ],
      planPrompt: "Plan as he grows?",
      planOptions: [
        { t: "Recheck weight regularly and increase the ration as he grows, transitioning to adult food around maturity", score: 3, note: "Growth needs frequent reassessment." },
        { t: "Fix today's amount until he's an adult", score: 0, note: "A kitten outgrows a fixed ration quickly." },
        { t: "Switch to adult food now to save money", score: 1, note: "Too early — he still needs a growth diet." },
      ],
    },
    educate: {
      prompt: "Best client-education message?",
      options: [
        { t: "Feed a complete kitten diet at the calculated amount split into several small meals, and we'll reweigh him regularly to increase it as he grows.", score: 3, note: "Specific, staged and monitored." },
        { t: "Just give him a bit of adult food when he's hungry.", score: 0, note: "Wrong diet and uncontrolled portions." },
        { t: "Feed roughly twice a day and adjust if he looks thin.", score: 1, note: "Vague and reactive for a growing kitten." },
      ],
    },
  },
];
